import { useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Link,Button } from '@mui/material';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box>
        <Typography variant="h3" align="center" gutterBottom>
          Privacy Policy for LedgerX
        </Typography>
        
        <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ fontStyle: 'italic', mb: 4 }}>
          Effective Date: 16th June 2025
        </Typography>
         <Button
                  size="small"
                  sx={{
                    marginBottom:'12px',
                    backgroundColor: "primary.main",
                    ":hover": { background: "#502E61" },
                    color: "white",
                  }}
                  onClick={() => navigate("/Create_Account")}
                >
                  Back to Signup
                </Button>

        <Typography paragraph>
          LedgerX is a product of TheX TechVent Pvt Ltd ("we", "our", or "us"). This Privacy Policy explains how we collect, use, store, and protect your information when you use the LedgerX mobile and web application.
        </Typography>

        <Typography paragraph>
          By using LedgerX, you agree to this policy and give your consent to the practices described below.
        </Typography>

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mb: 3 }}>
            1. Data We Collect
          </Typography>
          
          <Typography variant="h5" sx={{ mt: 3, mb: 2 }}>
            a) Personal & Login Information
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <Typography component="li">Name, email, phone number</Typography>
            <Typography component="li">Login method (including Google sign-in)</Typography>
            <Typography component="li">Device information (OS, model), IP address, time of access</Typography>
          </Box>

          <Typography variant="h5" sx={{ mt: 3, mb: 2 }}>
            b) Financial & Transactional Data
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <Typography component="li">Ledger entries, invoices, payments, receipts</Typography>
            <Typography component="li">GST, TDS, and other tax-related details</Typography>
            <Typography component="li">Uploaded files (e.g., PDFs, images)</Typography>
          </Box>
        </Box>

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mb: 3 }}>
            2. Data Ownership & Access
          </Typography>
          <Typography paragraph>Your financial and transactional data is fully owned by you.</Typography>
          <Typography paragraph>We store your data securely, but we do not access, view, use, or analyze your financial records such as ledger balances, Profit & Loss, or transaction details.</Typography>
          <Typography paragraph>Our team is not authorized to view your financial data. It is strictly private and protected.</Typography>
        </Box>

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mb: 3 }}>
            3. How We Use Your Data
          </Typography>
          <Typography paragraph>We use limited data only for the following purposes:</Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <Typography component="li">To maintain and improve app performance</Typography>
            <Typography component="li">To offer support when you request it</Typography>
            <Typography component="li">To ensure legal compliance (such as for login audits or fraud prevention)</Typography>
            <Typography component="li">To enable integrations with other services only with your permission</Typography>
          </Box>
          <Typography paragraph>We never use your financial data for analytics, profiling, or third-party sharing.</Typography>
        </Box>

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mb: 3 }}>
            4. Login & System Data
          </Typography>
          <Typography paragraph>To enhance system stability, performance, and legal compliance, we collect:</Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <Typography component="li">Login timestamps and frequency</Typography>
            <Typography component="li">Device type and operating system</Typography>
            <Typography component="li">IP address and crash reports</Typography>
          </Box>
          <Typography paragraph>This data helps us maintain security, improve speed, and meet legal obligations.</Typography>
        </Box>

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mb: 3 }}>
            5. Data Deletion & Retention
          </Typography>
          <Typography paragraph>You can delete your account and data at any time.</Typography>
          <Typography paragraph>Once deleted, your data is removed from active systems unless required to be retained by law.</Typography>
          <Typography paragraph>We do not keep your data after deletion, unless legal or statutory obligations apply (e.g., for audit or compliance inquiries).</Typography>
          <Typography paragraph>Backups of data for tax or government compliance are your responsibility. LedgerX does not act as your statutory data retention system.</Typography>
        </Box>

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mb: 3 }}>
            6. Sharing of Data
          </Typography>
          <Typography paragraph>We do not share or sell your data to any third party.</Typography>
          <Typography paragraph>Data may only be shared in these limited scenarios:</Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <Typography component="li">With your explicit consent, for integrations you enable (e.g., CRM, analytics tools)</Typography>
            <Typography component="li">With third-party service providers (for hosting or support), bound by confidentiality</Typography>
            <Typography component="li">With government authorities, only if legally compelled</Typography>
          </Box>
        </Box>

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mb: 3 }}>
            7. GDPR & User Rights
          </Typography>
          <Typography paragraph>If you reside in the European Union or regions following GDPR, you have the right to:</Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <Typography component="li">Access, correct, or delete your personal data</Typography>
            <Typography component="li">Request restriction or objection to data processing</Typography>
            <Typography component="li">Request data in a portable format</Typography>
          </Box>
          <Typography paragraph>For any such request, contact us at support@ledgerx.biz</Typography>
        </Box>

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mb: 3 }}>
            8. Security Measures
          </Typography>
          <Typography paragraph>We use industry-standard practices including encryption, role-based access, and secure cloud storage to protect your data. Despite best efforts, no system can be fully immune to risks, and we advise you to use secure login credentials.</Typography>
        </Box>

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mb: 3 }}>
            9. Children's Privacy
          </Typography>
          <Typography paragraph>LedgerX is not intended for users under the age of 18. We do not knowingly collect data from minors.</Typography>
        </Box>

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mb: 3 }}>
            10. Changes to this Policy
          </Typography>
          <Typography paragraph>We may revise this policy from time to time. Major updates will be notified through the app or email. Continued use of the app after such changes indicates your acceptance.</Typography>
        </Box>

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mb: 3 }}>
            11. Contact Us
          </Typography>
          <Typography>TheX TechVent Pvt Ltd</Typography>
          <Typography>Email: support@ledgerx.biz</Typography>
          <Typography>
            Website: <Link href="https://ledgerx.biz" underline="hover">https://ledgerx.biz</Link>
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy;
